// src/hooks/use_inspection_logic.js

import { useEffect, useMemo, useState, useCallback } from "react";
import { fetchData, postData } from "../func/api";
import { useAuth } from "../contexts/AuthContext";
import { APPROVAL_STATUS, ROLE_UUID } from "../const/config";
import { formatteDateTimeZone } from "../func/util";
import dayjs from "dayjs";
import { defaultCheckSummary } from "../const/summary_defaults";
import { getApprovalStatus } from "../func/status_utils";
import { getIntensityPriority } from '../func/util';

export const useInspectionLogic = (uuid, inspectionType) => {
    const { currentUser } = useAuth();

    // State
    const [earthquake, setEarthquake] = useState(null);
    const [checkResult, setCheckResult] = useState([]);
    const [approval, setApproval] = useState([]);
    const [reportDetailList, setReportDetailList] = useState([]);
    const [mailInfo, setMailInfo] = useState({});
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const [createComplete, setCreateComplete] = useState(false);
    const [checkSummary, setCheckSummary] = useState(defaultCheckSummary);
    const [checkSummaryPrime, setCheckSummaryPrime] = useState({});
    const [mandatoryFacilitys, setMandatoryFacilitys] = useState([]);
    const [facilityDams, setFacilityDams] = useState([]);
    const [department, setDepartment] = useState([]);

    const [taskStatus, setTaskStatus] = useState(() => {
        const saved = localStorage.getItem("taskStatus");
        // savedが null の場合や、数値として無効な文字列だった場合にデフォルト値の15あるいは18を返す(新規ブラウザで開いた場合に発生する)
        if (saved !== null && Number.isFinite(Number(saved))) {
            return Number(saved);
        }
        return (inspectionType === 'primary' ? 15 : 18);
    });

    // 派生状態
    const approvalStatus = useMemo(() => getApprovalStatus(approval), [approval]);
    const isInputter = approvalStatus === APPROVAL_STATUS.REMAND || approvalStatus === APPROVAL_STATUS.INIT;
    const isReviewer = approvalStatus === APPROVAL_STATUS.UNAPPROVED && currentUser?.role !== ROLE_UUID.VIEWER;
    const isReadOnly = approvalStatus === APPROVAL_STATUS.APPROVED || approvalStatus === APPROVAL_STATUS.UNAPPROVED;
    const isApprovedNotSent = approvalStatus === APPROVAL_STATUS.APPROVED && !earthquake?.[`${inspectionType}_inspection_sent_at`];
    const isSent = !!earthquake?.[`${inspectionType}_inspection_sent_at`];
    const isValidTask = taskStatus >= (inspectionType === 'primary' ? 15 : 18);

    // 共通のハンドラー
    const handleRestart = useCallback(async () => {
        try {
            localStorage.setItem("taskStatus", "0");
            await postData("api/post_report_stop", {
                earthquake_uuid: earthquake.uuid,
                initial_report_uuid: earthquake.initial_report_uuid,
                user_uuid: currentUser.id,
            });
            window.location.href = "/initial_task";
        } catch (e) {
            console.error(e);
        }
    }, [earthquake, currentUser]);

    // データ取得ロジック
    useEffect(() => {
        if (!uuid) return;
        const fetchAll = async () => {
            setLoading(true);
            try {
                const eq = await fetchData(`api/get_earthquake/${uuid}`);
                eq.initial_report_sent_at = (eq.initial_report_sent_at !== null) ? formatteDateTimeZone(eq.initial_report_sent_at) : null;
                setEarthquake(eq);

                const getSummaryUrl = (type) => `api/get_${type}_inspection_summary/${uuid}?is_init=${eq[`${type}_inspection_uuid`] === null}`;
                const getApprovalUrl = (type) => `api/get_${type}_inspection_approval/${uuid}`;

                const [cs, cr, ap, mf, fd, dl, dp] = await Promise.all([
                    fetchData(getSummaryUrl(inspectionType)),
                    fetchData(`api/get_check_result/${uuid}?is_init=${eq.initial_report_uuid === null}`),
                    fetchData(getApprovalUrl(inspectionType)),
                    fetchData('api/get_mandatory_facility'),
                    fetchData('api/get_facility_dam'),
                    fetchData(`api/get_dam_report_detail_list/${uuid}`),
                    fetchData(`api/get_department`)
                ]);

                const mandatory_facility_uuids = mf.map(facility => facility.uuid);
                setMandatoryFacilitys(mandatory_facility_uuids);
                const facility_dam_uuids = fd.map(facility => facility.uuid);
                setFacilityDams(facility_dam_uuids);
                setReportDetailList(dl);
                setDepartment(dp.name);

                // secondaryの場合のみprimaryのサマリーを取得
                let csp = null;
                if (inspectionType === 'secondary') {
                    csp = await fetchData(getSummaryUrl('primary'));
                    setCheckSummaryPrime(csp);
                }

                // checkSummaryの初期化ロジックを修正
                let updatedCheckSummary = { ...defaultCheckSummary, ...cs };

                if (inspectionType === 'primary' && !isReviewer) {
                    // ダム地点震度
                    const filteredResults = cr.filter(
                        (item) => facility_dam_uuids.includes(item.facility_uuid)
                    );
                    // フィルタリングされたデータの中から、最大震度のレコードを1つ取得
                    const highestIntensityRecord = filteredResults.length > 0
                        ? filteredResults.reduce((max, current) => {
                            const currentPriority = getIntensityPriority(current.intensity);
                            const maxPriority = getIntensityPriority(max.intensity);
                            return currentPriority > maxPriority ? current : max;
                        }, filteredResults[0])  // 初期値を配列の最初の要素に設定
                        : null;

                    // nullチェックを追加
                    if (highestIntensityRecord) {
                        updatedCheckSummary.intensity = highestIntensityRecord.intensity;
                        updatedCheckSummary.reason = `${eq.name}(最大震度${eq.max_intensity})発生に伴う臨時点検`;
                        updatedCheckSummary.inspection_date = dayjs().format("YYYY/MM/DD");
                    }
                } else if (inspectionType === 'secondary' && !isReviewer) {
                    const init = eq[`${inspectionType}_inspection_uuid`] === null;
                    // 初回はstart_timeとend_time以外をcspで更新し、start_timeとend_timeはcsで更新
                    if (init) {
                        const { start_time, end_time, ...cspWithoutTime } = csp;
                        const { start_time: csStartTime, end_time: csEndTime } = cs;
                        updatedCheckSummary = {
                            ...cspWithoutTime,
                            start_time: csStartTime,
                            end_time: csEndTime,
                        };
                    }
                }

                setCheckSummary(updatedCheckSummary);
                setCheckResult(cr);
                setApproval(ap);

                // メール情報取得 (primary inspectionのみ)
                const status = getApprovalStatus(ap);
                if (
                    (inspectionType === 'primary' || inspectionType === 'secondary') &&
                    status === APPROVAL_STATUS.APPROVED &&
                    !eq[`${inspectionType}_inspection_sent_at`]
                ) {
                    const name = encodeURIComponent(currentUser?.name || '');
                    const dept = encodeURIComponent(dp?.name || '');
                    const getMailUrl = (type) => `api/get_mail_${type}_inspection?user_name=${name}&user_department=${dept}&earthquake_uuid=${uuid}`;
                    const mail = await fetchData(getMailUrl(inspectionType));
                    setMailInfo(mail);
                }
            } catch (e) {
                console.error('fetch error:', e);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, [uuid, currentUser, inspectionType, isReviewer]);

    // taskStatus永続化
    useEffect(() => localStorage.setItem("taskStatus", String(taskStatus)), [taskStatus]);

    // 作成フラグリセット
    useEffect(() => setCreateComplete(false), [earthquake, checkResult, checkSummary]);

    // 返すべき値を返す
    return {
        earthquake,
        setEarthquake,
        checkResult,
        approval,
        reportDetailList,
        mailInfo,
        loading,
        open,
        setOpen,
        isChecked,
        setIsChecked,
        createComplete,
        setCreateComplete,
        checkSummary,
        setCheckSummary,
        checkSummaryPrime,
        setCheckSummaryPrime,
        mandatoryFacilitys,
        setMandatoryFacilitys,
        facilityDams,
        setFacilityDams,
        taskStatus,
        setTaskStatus,
        approvalStatus,
        isInputter,
        isReviewer,
        isReadOnly,
        isApprovedNotSent,
        isSent,
        isValidTask,
        handleRestart,
        department
    };
};